/**
 * Structured Error Types
 *
 * This module defines the standard structure for API errors with rich details.
 * The server sends errors with a detailType that tells the client how to render them.
 *
 * Architecture:
 * - Server defines the error structure with detailType
 * - Client has standard renderers for each detailType
 * - Projects can register custom processors for specific error codes
 */

// Multi-language text support
export type MultiLangText = {
  es: string;
  en: string;
  pt?: string;
};

// Helper to check if a value is MultiLangText
export const isMultiLangText = (value: unknown): value is MultiLangText => {
  return (
    typeof value === "object" &&
    value !== null &&
    "es" in value &&
    "en" in value
  );
};

// Extract text from MultiLangText or string based on current language
export const resolveText = (
  value: string | MultiLangText | undefined,
  language: "es" | "en" | "pt"
): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value[language] || value.es || "";
};

// Standard detail types that the client knows how to render
export type ErrorDetailType =
  | "item-list" // List of items (e.g., missing scores)
  | "field-errors" // Field-level validation errors
  | "key-value" // Key-value pairs
  | "message" // Simple message
  | "custom"; // Custom rendering (requires registered processor)

// Item in an item-list
export interface ErrorItem {
  id?: string; // Optional ID for client-side enrichment
  label: string | MultiLangText; // Display text
  [key: string]: unknown; // Additional data for custom processors
}

// Field error in field-errors
export interface ErrorFieldError {
  field: string;
  message: string | MultiLangText;
  constraint?: string;
}

// Entry in key-value
export interface ErrorKeyValueEntry {
  key: string | MultiLangText;
  value: string | MultiLangText;
}

// The structured error meta that the server sends
export interface StructuredErrorMeta {
  // How the client should render this error
  detailType: ErrorDetailType;

  // For item-list: list of affected items
  items?: ErrorItem[];

  // For field-errors: list of field-level errors
  fieldErrors?: ErrorFieldError[];

  // For key-value: list of key-value pairs
  entries?: ErrorKeyValueEntry[];

  // For message: additional message text
  message?: string | MultiLangText;

  // For custom: raw data for custom processors
  raw?: Record<string, unknown>;

  // Legacy support: validationErrors array
  validationErrors?: Array<Record<string, unknown>>;

  // Other meta fields (entityName, field, etc.)
  [key: string]: unknown;
}

// Check if meta has structured error format
export const isStructuredErrorMeta = (
  meta: unknown
): meta is StructuredErrorMeta => {
  return typeof meta === "object" && meta !== null && "detailType" in meta;
};
